import React, { useEffect, useState } from "react";
import { MapPin, Heart, Leaf, ArrowRight, Phone, Mail, Globe, BookOpen, Coffee, Home, Sparkles, Target, Baby, Flame, Users, Brain, Flower2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
// Removed Footer import - App.tsx handles global footer
// GoogleAnalytics is loaded globally in App.tsx
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import treatmentImage from '@/assets/treatment.jpg';

const BirthPrepPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Birth Preparation & Labour Support | Currimundi Clinic";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Prepare for birth with Chinese Medicine in Currimundi. Labour support, acupuncture, and birth preparation on the Sunshine Coast.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Birth Preparation & Labour Support | Currimundi Clinic");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Prepare for birth with Chinese Medicine in Currimundi. Labour support, acupuncture, and birth preparation on the Sunshine Coast.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/birth-prep");
    }
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/birth-prep-chinese-medicine.jpg");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Birth Preparation & Labour Support | Currimundi Clinic");
    }
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Birth Preparation & Labour Support",
      "image": "https://womenandtheway.com.au/birth-prep-chinese-medicine.jpg",
      "description": "Prepare for birth with Chinese Medicine in Currimundi. Labour support, acupuncture, and birth preparation on the Sunshine Coast.",
      "url": "https://womenandtheway.com.au/birth-prep",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Birth Preparation, Labour Support, Acupuncture, Chinese Medicine, Pregnancy Care",
      "areaServed": "Sunshine Coast, Currimundi, Caloundra, Maroochydore",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Birth Preparation & Labour Support
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Prepare your body, calm your mind, and trust in your inner strength.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  As your due date approaches, it's natural to feel both excited and unsure. Whether this is your first birth or your fifth, preparing your body, mind, and energy can make all the difference. At Women and the Way, we offer birth preparation sessions grounded in Chinese medicine to help you feel ready - physically, emotionally, and spiritually. Our <a href="/pregnancy" className="text-amber-600 hover:text-amber-700 underline">pregnancy care</a> and <a href="/postpartum" className="text-amber-600 hover:text-amber-700 underline">postpartum support</a> complement your birth preparation journey.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Our treatments help position your baby optimally, soften tension, reduce anxiety, and encourage the natural onset of labour when the time is right.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={treatmentImage} 
                  alt="Birth preparation and labour support consultation with Chinese Medicine in Currimundi clinic" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>
        {/* Why Birth Prep Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Baby className="w-8 h-8 text-sage-600" />
                Why Birth Prep Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Chinese medicine, birth is the peak of a woman's energetic cycle - a moment where all channels open, Qi must move freely, and the body relies on its deepest reserves of Blood, Yin, and Jing (essence). Preparing for labour helps harmonise these systems and set the stage for a smooth, empowered birthing experience.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Starting from 34–36 weeks, birth preparation acupuncture can gently ready your body and support the natural cascade of labour hormones.
              </p>
            </div>
          </div>
        </section>
        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                In-Clinic and At-Home Birth Preparation Support
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Book a calming series of weekly birth prep sessions starting around 36 weeks.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Acupuncture to ripen the cervix, relax the pelvis, regulate hormones, and reduce labour anxiety</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Herbal support (as needed) for digestion, sleep, and Yin tonification</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🔥</span>
                      <p className="text-sage-700">Moxa therapy (especially if baby is breech) to support optimal positioning</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌀</span>
                      <p className="text-sage-700">Breathwork, mindfulness, and visualisation for emotional preparation and confidence</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">At-Home Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4 font-medium">
                    We can support your preparation from the comfort of your home.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">In-home acupuncture for those at 38+ weeks or with mobility or travel limitations</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🧘‍♀️</span>
                      <p className="text-sage-700">Education on acupressure points for labour, fear release, and partner support</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📚</span>
                      <p className="text-sage-700">Personalised birth prep guides and rituals</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        {/* When to Start */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6">
                When to Start
              </h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Flower2 className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Week 34–35</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Begin gentle regulation and pelvic preparation</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Week 36–37</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Build momentum - focus on ripening cervix and calming nerves</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Week 38–40+</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Encourage readiness and natural onset of labour</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Post-dates support</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Gentle, non-invasive encouragement for spontaneous labour</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>
        {/* Birth Support Focus Areas */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6">
                Birth Support Focus Areas
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { western: "Back pain, pelvic pressure", chinese: "Qi stagnation, Kidney deficiency" },
                { western: "Breech or posterior baby", chinese: "Cold in the uterus, Qi constraint" },
                { western: "Labour anxiety", chinese: "Heart-Kidney imbalance, Shen disturbance" },
                { western: "Insomnia or racing thoughts", chinese: "Yin deficiency, Heart Fire" },
                { western: "Cervix not ripening", chinese: "Blood stasis, Qi stagnation" },
                { western: "Baby 'not engaged'", chinese: "Liver Qi constraint, lack of downward movement" },
                { western: "Overdue pregnancy", chinese: "Qi and Blood stagnation" }
              ].map((condition, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-sage-800 mb-2">{condition.western}</h3>
                    <p className="text-sage-600 text-sm italic">{condition.chinese}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* What Makes Our Approach Unique */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6">
                What Makes Our Approach Unique
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: "🫶", title: "Whole-woman care", desc: "We see you - not just your cervix or baby position. This is a sacred rite of passage." },
                { icon: "🌿", title: "Natural and non-invasive", desc: "We gently support your body's timing and rhythm - never force it" },
                { icon: "📚", title: "Tools you can take home", desc: "We teach you and your partner acupressure points, breathing techniques, and rituals for labour" },
                { icon: "🌀", title: "Emotional and energetic support", desc: "Because preparing the body is only part of the story - your mindset matters too" }
              ].map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                    <p className="text-sage-700">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* Support Tools */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6">
                Birth Support Tools in Clinic
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Target, title: "Acupressure charts", desc: "And instructions for you + partner" },
                { icon: Coffee, title: "Labour prep teas", desc: "And gentle herbal support" },
                { icon: Flame, title: "Moxa sticks for home use", desc: "With instructions" },
                { icon: Leaf, title: "Castor oil compress protocols", desc: "If appropriate" },
                { icon: BookOpen, title: "Recommended reading", desc: "Gentle Birth, Gentle Mothering, Birth With Confidence, The Birth Partner" }
              ].map((tool, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                    <p className="text-sage-700 text-sm">{tool.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6">
              Book Your Birth Preparation Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              You were made for this. Let's help you feel centred, confident, and connected as you approach birth.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? 'Booking...' : 'Book Birth Preparation Session'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        {/* Footer removed - App.tsx handles global footer */}
        <ScrollDownArrow />
        <ReturnToTop />
      </div>
  );
};

export default BirthPrepPage; 